import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  PermissionValues,
  RoutineFilterModel,
  useCurrentAccount,
  useCurrentWorkspace,
  useRoutineFilter,
  WorkspaceType
} from '@gripp/shared-logic';
import {
  Colors,
  FilterType,
  RoutineExecutionProps,
  RoutineModal,
  RoutineModules,
  RoutineSearchInput,
  Spacing,
} from '@gripp/shared-ui';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleProp, StyleSheet, Text, View } from 'react-native';
import { Button } from 'react-native-paper';
import { useNavigate } from 'react-router-dom';

import { FilterBar } from '../../shared/components/filter/filterBar';
import { ModalButton } from '../../shared/components/modalButton';
import { CopyToWorkspaceButton } from './copyToWorkspaceButton';
import { DeleteRoutineButton } from './deleteRoutineButton';
import { RoutineAssetsForm } from './routineAssetsForm';
import { RoutineRowActions } from './routineRowActions';
import { RoutineList } from './routinesList';

export const Routines = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: routineFilter } = useRoutineFilter();
  const { account } = useCurrentAccount();

  const [rowsSelected, setRowsSelected] = useState(Array<any>);
  const [openRoutinePreviewModal, setOpenRoutinePreviewModal] = useState(false);
  const [executeModalProps, setExecuteModalProps] = useState<
    RoutineExecutionProps | undefined
  >();

  const impersonatingWorkspace = useCurrentWorkspace(true);
  const currentWorkspace = useCurrentWorkspace();
  const workspace = useCurrentWorkspace(true);

  const showCheckboxColumn = useMemo(
    () => !!impersonatingWorkspace,
    [impersonatingWorkspace]
  );

  const filter = useMemo(() => {
    const filter: RoutineFilterModel =
      routineFilter ?? new RoutineFilterModel();
    filter.workspaceId = currentWorkspace?.id;
    return filter.getGraphQLFilter();
  }, [routineFilter, currentWorkspace?.id]);

  const updateRowsSelected = useCallback((rows: Array<any>) => {
    setRowsSelected(rows);
  }, []);

  const isGlobalAdmin = useMemo(
    () => !!(account && account.hasPermission(PermissionValues.globalAdmin)),
    [account]
  );

  const canViewRoutines = useMemo(
    () =>
      !!(account && account.hasPermission(PermissionValues.routinespage.read)),
    [account]
  );

  const RoutineGridHeader = () =>
    showCheckboxColumn ? (
      <View style={styles.headerContainer}>
        <RoutineRowActions
          routineIds={rowsSelected}
          workspaceId={workspace?.id}
        />
      </View>
    ) : null;

  const getLabel = (labelStyle: StyleProp<any>, label: string) => {
    return <Text style={[labelStyle, styles.cellText]}>{label}</Text>;
  };

  const AssetsButton = (data: any) => {
    return (
      <ModalButton
        label=""
        customButton={
          <View style={[styles.iconContainer]}>
            <MaterialCommunityIcons
              name="qrcode"
              color={Colors.blackText}
              size={16}
              style={styles.icon}
            />
            {getLabel(styles.countText, data.assetsCount)}
          </View>
        }
        modalComponent={({ onComplete }: { onComplete: () => void }) => {
          return (
            <RoutineAssetsForm
              routineId={data.routineId}
              title={data.name}
              showCheckboxColumn={showCheckboxColumn}
              onComplete={onComplete}
            />
          );
        }}
        containerStyle={styles.modalContainer}
      />
    );
  };

  const rightFormButton = executeModalProps?.routine && isGlobalAdmin && (
    <Button
      style={styles.button}
      labelStyle={styles.buttonLabel}
      onPress={() => {
        navigate(`${executeModalProps?.routine?.id}/edit`);
      }}
      buttonColor={Colors.primary}
    >
      {t('topNav.modify')}
    </Button>
  );

  const footer = executeModalProps?.routine && isGlobalAdmin && (
    <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
      <DeleteRoutineButton
        onSelected={() => setOpenRoutinePreviewModal(false)}
        workspaceRoutine={executeModalProps.routine}
      />
      <CopyToWorkspaceButton
        onSelected={() => setOpenRoutinePreviewModal(false)}
        workspaceRoutine={executeModalProps.routine}
      />
    </View>
  );

  const handleRoutineRowClick = (row: any) => {
    const {
      assetsCount,
      allAssetsCount,
      submissionsCount,
      lastSubmitted,
      updatedAt,
      ...rest
    } = row || {};

    setOpenRoutinePreviewModal(true);
    setExecuteModalProps({
      workspaceAsset: {
        workspace: currentWorkspace,
      },
      module: RoutineModules?.[row.type],
      routine: { ...rest },
      preview: true,
    });
  };

  return (
    <View style={styles.container}>
      <View style={styles.filtersContainer}>
        <View style={styles.searchContainer}>
          <RoutineSearchInput />
        </View>
        <FilterBar filterButtonTypes={[FilterType.ROUTINECOLUMNS]} />
      </View>
      <RoutineList
        filter={filter}
        updateRowsSelected={updateRowsSelected}
        showCheckboxColumn={showCheckboxColumn}
        showImpersonateWorkspace={canViewRoutines}
        headerContentComponent={<RoutineGridHeader />}
        assetsButton={AssetsButton}
        onRowClick={handleRoutineRowClick}
        isProducerWorkspace={currentWorkspace?.type === WorkspaceType.Producer}
      />
      {openRoutinePreviewModal && executeModalProps && (
        <RoutineModal
          {...executeModalProps}
          visible={openRoutinePreviewModal}
          onCanceled={() => {
            setOpenRoutinePreviewModal(false);
            setExecuteModalProps(undefined);
          }}
          onCompleted={async () => {
            setOpenRoutinePreviewModal(false);
          }}
          rightFormButton={rightFormButton}
          footer={footer}
          showPortal
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
  },
  filtersContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    height: 44,
    marginBottom: 20,
  },
  searchContainer: {
    width: 390,
    marginRight: 20,
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  iconContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  icon: {
    marginRight: 2,
  },
  countText: {
    fontSize: 16,
  },
  cellText: {
    fontSize: 16,
    fontWeight: '500',
    flexWrap: 'nowrap',
  },
  modalContainer: {
    width: 640,
    height: '80%',
    minHeight: 450,
    alignSelf: 'center',
    backgroundColor: Colors.primaryGrayBackground,
    borderRadius: 10,
  },
  button: {
    borderRadius: 8,
    width: '100%',
    maxWidth: 100,
    height: 44,
    justifyContent: 'center',
    marginRight: Spacing.baseMargin.base,
  },
  buttonLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.white,
  },
});
